const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/selectors/metakatSelectors.ts';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CommonStoreState } from '../reducers';
import { ReducerState } from '../defs/actionTypes';
import { MetaKatItem } from '../defs/kisim';
import { getData, RequestStatus, useRemoteData2 } from '../utils/redux4Requests';
import { loadMetaKatBlock, loadMetaKatChildren, loadMetaKatRecord } from '../actions/metakatActions';
import { createSelector } from 'reselect';

const mmk = 'multiMetaKat';
export const metaKatLoadingRoot = (state: CommonStoreState) => state.metakat.get('loading');
export const metaKatIsLoadingRoot = (state: CommonStoreState) => state.metakat.get('loading').size !== 0;
export const metaKatStructureRoot = (state: CommonStoreState) => state.metakat.get('structure');
export const metaKatElementsRoot = (state: CommonStoreState) => state.metakat.get('elements');
export const metaKatLookupsRoot = (state: CommonStoreState) => state.metakat.get('lookups');
export const multiMetaKatKeys = (state: CommonStoreState): { [key: string]: string } =>
    state.metakat.get('multiMKKeys')?.toJS() || {};
export const multiMetaKatKeysRequestStatus = (state: CommonStoreState): RequestStatus =>
    state.metakat.get('mmkRequestStatus') || RequestStatus.PENDING;
export const multiMetaKatPending = (state: CommonStoreState): string => state.metakat.getIn(['loading', mmk]);
export const multiMetaKatRecords = (state: CommonStoreState): any => state.metakat.get(mmk);
export const multiMetaKatRecordsCount = (state: CommonStoreState): number =>
    typeof state.metakat.get(mmk)?.count === 'function' ? state.metakat.get(mmk)?.count() : 0;
export const multiMetaKatRecordsByKey = (state: CommonStoreState, key: string) => state.metakat.getIn([mmk, key]);

const selectReducerState = (state: CommonStoreState): ReducerState => state.metakat;

const makeSelectMetaKatBlock = (iCode: string) =>
    createSelector([selectReducerState], (state: ReducerState): MetaKatItem[] | undefined =>
        getData(state, ['block', iCode])
    );

const makeSelectMetaKatChildren = (iCode: string) =>
    createSelector([selectReducerState], (state: ReducerState): MetaKatItem[] | undefined =>
        getData(state, ['children', iCode])
    );

export function useMetaKatBlock(iCode: string, lookup: boolean = false, fields?: string[]) {
    return useRemoteData2('metakat', {
        canRequest: !!iCode,
        request: dispatch => dispatch(loadMetaKatBlock(iCode, lookup, undefined, true, fields)),
        keys: ['block', iCode],
        select: makeSelectMetaKatBlock(iCode),
    });
}

export function useMetaKatChildren(iCode: string, lookup: boolean = false, fields: string[] = []) {
    return useRemoteData2('metakat', {
        canRequest: !!iCode,
        request: dispatch => dispatch(loadMetaKatChildren(iCode, fields, lookup)),
        keys: ['children', iCode],
        select: makeSelectMetaKatChildren(iCode),
    });
}

// PCODE is mandatory: the reducer drops records that arrive without it (see loadMetaKatRecordHandlers).
const RECORD_NAME_FIELDS = ['ICODE', 'PCODE', 'NAME', 'KAT', 'FKEY'];

/**
 * Resolves a MetaKat record's NAME from its ICODE, loading the record if it is not in the store yet. Useful wherever
 * only an ICODE was persisted — a filter restored from a link, for instance — and a readable label is needed.
 *
 * Unlike its siblings this does not use useRemoteData2: the record handlers store the payload directly under
 * ['elements', icode] without a request status, so there is nothing for useRemoteData2 to read.
 *
 * NOTE: reading a record directly bypasses any rights check an endpoint would apply. Use it to display a name, never to
 * decide whether the entry may be selected.
 */
export function useMetaKatRecordName(iCode: string | null | undefined): string | undefined {
    const dispatch = useDispatch();

    const name = useSelector((state: CommonStoreState) =>
        iCode ? (state.metakat.getIn(['elements', iCode, 'NAME']) as string | undefined) : undefined
    );

    const isMissing = Boolean(iCode) && name === undefined;

    useEffect(() => {
        if (iCode && isMissing) dispatch(loadMetaKatRecord(iCode, RECORD_NAME_FIELDS));
    }, [iCode, isMissing]);

    return name;
}
